import type { BlogPost, BlogPostSummary } from "../data/content";
import type { Locale } from "../i18n";

type PostsResponse = {
  posts: BlogPostSummary[];
};

type PostResponse = {
  post: BlogPost;
};

export type PostLocale = Locale;

export class ApiRequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

async function getJson<T>(
  url: string,
  locale: PostLocale,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Accept-Language": locale
    },
    signal
  });

  if (!response.ok) {
    throw new ApiRequestError(
      response.status,
      `Request failed with status ${response.status}`
    );
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiRequestError(502, "The API returned an unreadable response.");
  }
}

export async function getPosts(
  locale: PostLocale = "en",
  signal?: AbortSignal
): Promise<BlogPostSummary[]> {
  const data = await getJson<PostsResponse>(
    `/api/posts?limit=12&locale=${locale}`,
    locale,
    signal
  );
  return data.posts;
}

export async function getPost(
  slug: string,
  locale: PostLocale = "en",
  signal?: AbortSignal
): Promise<BlogPost> {
  const data = await getJson<PostResponse>(
    `/api/posts/${encodeURIComponent(slug)}?locale=${locale}`,
    locale,
    signal
  );
  return data.post;
}
